import { useCallback, useState } from 'react';

import { logError } from '@hotjar/webapp/lib/logger';
import { useChurnSurvey as serviceUseChurnSurvey } from '@hotjar/webapp/hooks/bridging/useChurnSurvey';
import type { DefaultChurnSurvey } from '../churn-survey/types';
import type { GetSurveyResponseQuestion } from '../churn-survey/dto';

export interface UseChurnSurvey {
  getSurvey: () => void;
  submitSurvey: (data: DefaultChurnSurvey) => void;
  surveyStatus: SurveyStatus;
  surveyQuestions: GetSurveyResponseQuestion[] | null;
}

export enum SurveyStatus {
  NOT_LOADING,
  FETCHING,
  SUCCESS,
  ERROR,
}

export function useChurnSurvey(): UseChurnSurvey {
  const { get: getChurnSurvey, submit: submitChurnSurvey } = serviceUseChurnSurvey();

  const [surveyStatus, setSurveyStatus] = useState<SurveyStatus>(SurveyStatus.NOT_LOADING);
  const [surveyQuestions, setSurveyQuestions] = useState<GetSurveyResponseQuestion[] | null>(null);

  const getSurvey = useCallback(async () => {
    setSurveyStatus(SurveyStatus.FETCHING);

    try {
      const {
        survey: {
          content: { questions },
        },
      } = await getChurnSurvey();
      setSurveyQuestions(questions);
      setSurveyStatus(SurveyStatus.SUCCESS);
    } catch (error) {
      // @ts-expect-error fix catch error type
      logError(error);
      setSurveyStatus(SurveyStatus.ERROR);
    }
  }, [getChurnSurvey, setSurveyQuestions]);

  const submitSurvey = useCallback(
    (finalData: DefaultChurnSurvey) => {
      if (!surveyQuestions) {
        return;
      }
      submitChurnSurvey(finalData, surveyQuestions);
    },
    [submitChurnSurvey, surveyQuestions],
  );

  return {
    getSurvey,
    surveyStatus,
    surveyQuestions,
    submitSurvey,
  };
}
